// store/baterias/index.js
// ───────────────────────────────────────────────────────────
// Store AISLADO para carrito de BATERÍAS
// - Replica estados y mutations del carrito original via ALIAS 1:1
// - Persistencia en localStorage con claves namespaced leon_bat_*
// - SSR-safe (Nuxt 3) y reglas de negocio configurables por ENV
// - Migración automática desde carrito clásico (fullcercano, installType, carplate)
// ───────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────
// STORAGE KEYS (namespace propio)
// ───────────────────────────────────────────────────────────
const STORAGE_CART_ITEMS           = 'leon_bat_cart_items';
const STORAGE_CART_REMOVED         = 'leon_bat_removed_items';
const STORAGE_BATTERY_INSTALL_TYPE = 'leon_bat_battery_install_type';
const STORAGE_SCHEDULE             = 'leon_bat_schedule';
const STORAGE_ADDRESS              = 'leon_bat_address';
const STORAGE_ADDRESSES_OBJ        = 'leon_bat_addresses';
const STORAGE_PERSONAL             = 'leon_bat_personal';
const STORAGE_FLAGS                = 'leon_bat_flags';
const STORAGE_META                 = 'leon_bat_meta';
const STORAGE_PASOAPASO            = 'leon_bat_pasoapaso';
const STORAGE_localSeleccionado      = 'leon_bat_localSeleccionado';
const STORAGE_PAYMENT              = 'leon_bat_payment';
const STORAGE_GIFTCARD             = 'leon_bat_giftcard';
const STORAGE_GIFTS                = 'leon_bat_gifts';
const STORAGE_REGALO_RULETA        = "leon_regalo_ruleta";

const STORAGE_SERVICES             = 'leon_bat_services';
const STORAGE_LOCALPIVOTE          = 'leon_bat_localpivote';
const STORAGE_FULLCERCANO          = 'leon_bat_fullcercano';
const STORAGE_LOCAL_CAMBIO         = 'leon_bat_local_cambio';
const STORAGE_MEASURE              = 'leon_bat_measure';
const STORAGE_UBICATION            = 'leon_bat_ubication';
const STORAGE_UBICATION_CONFIRMED  = 'leon_bat_ubication_confirmed';
const STORAGE_SHOPPING_STEP        = 'leon_bat_shoppingStep';
const STORAGE_ULTIMO_PASO          = 'leon_bat_ultimo_paso';
const STORAGE_ADDRESS_WAS_CHANGED  = 'leon_bat_addressWasChanged';
// ───────────────────────────────────────────────────────────
// Config por ENV (con defaults robustos para baterías)
// ───────────────────────────────────────────────────────────
const getExpirationMinutes = () => {
  const raw = (import.meta?.env && import.meta.env.NUXT_TIME_PRODUCT_RESET) || '30';
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 30;
};
const getMaxDistinct = () => {
  const raw = (import.meta?.env && import.meta.env.NUXT_BATT_MAX_DISTINCT) || '1';
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 1; // por diseño: 1 SKU en baterías
};
const getMaxUnits = () => {
  const raw = (import.meta?.env && import.meta.env.NUXT_BATT_MAX_UNITS) || '2';
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 2; // por diseño: máximo 2 unidades
};

// ───────────────────────────────────────────────────────────
// Helpers localStorage (SSR-safe)
// ───────────────────────────────────────────────────────────
const getJSON = (key, fallback) => {
  try {
    if (!import.meta.client) return fallback;

    const raw = localStorage.getItem(key);

    // Si no hay valor o es basura conocida, usa fallback
    if (!raw || raw === 'undefined' || raw === 'null') {
      localStorage.removeItem(key); // opcional pero recomendable: limpiamos clave corrupta
      return fallback;
    }

    return JSON.parse(raw);
  } catch (e) {
    console.warn('getJSON error', key, e);
    // si está corrupto, lo limpiamos para no seguir pegando en cada load
    try {
      if (import.meta.client) localStorage.removeItem(key);
    } catch {}
    return fallback;
  }
};

const setJSON = (key, value) => {
  try {
    if (import.meta.client) localStorage.setItem(key, JSON.stringify(value));
  } catch (e) { console.warn('setJSON error', key, e); }
};
const getStr = (key, fallback = null) => {
  try { return import.meta.client ? (localStorage.getItem(key) ?? fallback) : fallback; }
  catch { return fallback; }
};
const setStr = (key, value) => {
  try { if (import.meta.client) localStorage.setItem(key, String(value)); }
  catch (e) { console.warn('setStr error', key, e); }
};
const removeKey = (key) => {
  try { if (import.meta.client) localStorage.removeItem(key); }
  catch (e) { console.warn('removeKey error', key, e); }
};
const hasCodigo = (product) => !!(product && typeof product === 'object' && 'codigo' in product);

// ───────────────────────────────────────────────────────────
// Helpers LEGACY (lee claves del carrito clásico)
// ───────────────────────────────────────────────────────────
const getLegacyStr = (key, fallback = null) => {
  try { return import.meta.client ? (localStorage.getItem(key) ?? fallback) : fallback; }
  catch { return fallback; }
};
const getLegacyJSON = (key, fallback = null) => {
  try {
    if (!import.meta.client) return fallback;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};

// ───────────────────────────────────────────────────────────
// Timer módulo-scope
// ───────────────────────────────────────────────────────────
let expirationTimer = null;
const clearTimer = () => { if (expirationTimer) { clearTimeout(expirationTimer); expirationTimer = null; } };

// Helper interno del timer (evita mutaciones dentro de mutaciones)
const startExpirationTimerInternal = (state) => {
  if (!import.meta.client) return;
  clearTimer();
  const minutes = getExpirationMinutes();
  if (!state.meta.cartTimestamp || state.items.length === 0) return;

  const now = Date.now();
  const target = state.meta.cartTimestamp + minutes * 60 * 1000;

  const limpiarcarrito = () => {
    state.items = [];
    state.products = []; // alias
    setJSON(STORAGE_CART_ITEMS, state.items);
    state.meta.cartTimestamp = null;
    state.cartTimestamp = null; // alias
    setJSON(STORAGE_META, state.meta);
  };

  if (target > now) {
    expirationTimer = setTimeout(() => { limpiarcarrito(); clearTimer(); }, target - now);
  } else {
    limpiarcarrito();
  }
};

// ───────────────────────────────────────────────────────────
// STATE – incluye canónico + alias 1:1 para compatibilidad
// ───────────────────────────────────────────────────────────
const state = () => ({
  // Canónico (baterías)
  items: [],
  removed_items: [],
  pasoapaso: 0,
  batteryInstallType: '',
  //install: { type: 1, batteryInstallType: '' },
  localSeleccionado: { local_id: null, local_nombre: '', direccion: '', region: '', commune: '' },
  calendar: { checkStoreAvailability: false, scheduleType: 1, date: new Date().toISOString().slice(0, 10), time: null, totalTime: 0, tempario: "" },
  address: { region: null, commune: null, address: '', details: '', comments: '', latitude: null, longitude: null },
  personalInformation: { rut: '', name: '', lastName: '', surName: '', email: '', phone: '', carplate: '', patente_pais: '' },
  payment: { paymentId: '', paymentMethod: '' },
  flags: { acceptTerms: false, acceptPolitica: false, personalInfoSaved: false },
  meta: { cartTimestamp: null, transaction_uuid: null, isValidCarplate: false },

  // Alias (clon del carrito original)
  products: [],
  removedProducts: [],
  shoppingStep: 0,
  ultimo_paso: 0,
  region: null,
  commune: null,

  personalInfoSaved: false, // alias paralelo a flags.personalInfoSaved

  localpivote: {},
  fullcercano: {},
  local_cambio: false,

  ubication: { isLoading: false, latitude: null, longitude: null, address: "", region: "", commune: "" },
  ubication_confirmed: { isLoading: false, latitude: null, longitude: null, address: "", region: "", commune: "" },

  isValidCarplate: '', // alias (string/bool admitido)

  services: [],
  addresses: {
    showAddressOptions: false,
    showLeaveAddress: false,
    address: { region: null, commune: null, address: "", details: "", comments: "", latitude: null, longitude: null },
    leaveAddress: { region: null, commune: null, address: "", details: "", comments: "", latitude: null, longitude: null },
  },

  personalInformation: { rut: "", name: "", lastName: "", surName: "", email: "", phone: "", carplate: "", patente_pais: "" },

  giftcardInput: '',
  isGiftcardInfoLoading: false,
  giftcard: { itWasSend: false, isValid: false, code: "", uuid: "", descriptionCode: "" },

  gifts: [],
  regalo_ruleta: [],

  history: [],

  cartTimestamp: null,
  transaction_uuid: null,
  addressWasChanged: false,
});

// ───────────────────────────────────────────────────────────
// MUTATIONS (canónicas + ALIAS 1:1 del store original)
// ───────────────────────────────────────────────────────────
const mutations = {
  // Resets
  RESET_CART(state) {
    state.items = [];
    state.products = [];
    setJSON(STORAGE_CART_ITEMS, state.items);
  },

  RESET_ALL(state) {
    // limpia estado canónico
    state.items = [];
    state.removed_items = [];
    state.batteryInstallType = { batteryInstallType: '' };
    state.calendar = { checkStoreAvailability:false, scheduleType:1, date:new Date().toISOString().slice(0, 10), time:null, totalTime:0, tempario:"" };
    state.address = { region:null, commune:null, address:'', details:'', comments:'', latitude:null, longitude:null };
    state.personalInformation = { rut:'', name:'', lastName:'', surName:'', email:'', phone:'', carplate:'', patente_pais:'' };
    state.payment = { paymentId:'', paymentMethod:'' };
    state.flags = { acceptTerms:false, acceptPolitica:false, personalInfoSaved:false };
    state.meta = { cartTimestamp:null, transaction_uuid:null, isValidCarplate:false };
    state.pasoapaso = 0;
    state.localSeleccionado = { local_id:null, local_nombre:'', direccion:'', region:'', commune:'' };

    // alias
    state.products = [];
    state.removedProducts = [];
    state.shoppingStep = 0;
    state.ultimo_paso = 0;
    state.region = null;
    state.commune = null;
    state.personalInfoSaved = false;
    state.localpivote = {};
    state.fullcercano = {};
    state.local_cambio = false;
    state.ubication = { isLoading:false, latitude:null, longitude:null, address:"", region:"", commune:"" };
    state.ubication_confirmed = { isLoading:false, latitude:null, longitude:null, address:"", region:"", commune:"" };
    state.isValidCarplate = '';
    state.services = [];
    state.addresses = {
      showAddressOptions:false,
      showLeaveAddress:false,
      address: { region:null, commune:null, address:"", details:"", comments:"", latitude:null, longitude:null },
      leaveAddress: { region:null, commune:null, address:"", details:"", comments:"", latitude:null, longitude:null },
    };
    //state.personalInformation = { rut:"", name:"", lastName:"", surName:"", email:"", phone:"", carplate:"", patente_pais:"" };
    state.giftcardInput = '';
    state.isGiftcardInfoLoading = false;
    state.giftcard = { itWasSend:false, isValid:false, code:"", uuid:"", descriptionCode:"" };
    
    state.gifts = [];
    state.regalo_ruleta = [];

    state.history = [];
    state.cartTimestamp = null;
    state.transaction_uuid = null;
    state.addressWasChanged = false;

    // limpia storage del namespace
    [
      STORAGE_CART_ITEMS, STORAGE_CART_REMOVED, STORAGE_BATTERY_INSTALL_TYPE, STORAGE_SCHEDULE, STORAGE_ADDRESS,
      STORAGE_ADDRESSES_OBJ, STORAGE_PERSONAL, STORAGE_PAYMENT, STORAGE_FLAGS, STORAGE_META,
      STORAGE_PASOAPASO, STORAGE_localSeleccionado, STORAGE_LOCALPIVOTE, STORAGE_FULLCERCANO,
      STORAGE_LOCAL_CAMBIO, STORAGE_MEASURE, STORAGE_UBICATION,
      STORAGE_UBICATION_CONFIRMED, STORAGE_SHOPPING_STEP, STORAGE_ULTIMO_PASO, STORAGE_GIFTCARD,
      STORAGE_GIFTS, STORAGE_REGALO_RULETA, STORAGE_SERVICES, STORAGE_ADDRESS_WAS_CHANGED
    ].forEach(k => removeKey(k));

    clearTimer();
  },

  // Paso a paso
  SET_PASOAPASO(state, paso) {
    state.pasoapaso = Number.isFinite(paso) ? paso : 0;
    setJSON(STORAGE_PASOAPASO, state.pasoapaso);
  },

  // Carro (canónico)
  ADD_ITEM(state, { product, quantity = 1 }) {
    if (!hasCodigo(product)) { console.warn('ADD_ITEM: product inválido (sin "codigo")'); return; }
    const MAX_DISTINCT = getMaxDistinct();
    const MAX_UNITS    = getMaxUnits();
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const idx = state.items.findIndex(i => i?.product?.codigo === product.codigo);

    if (idx === -1) {
      if (state.items.length >= MAX_DISTINCT) {
        state.items = [{ product, quantity: Math.min(qty, MAX_UNITS) }];
      } else {
        state.items.push({ product, quantity: Math.min(qty, MAX_UNITS) });
      }
    } else {
      const totalUnits = state.items.reduce((t, i) => t + (i?.quantity || 0), 0);
      const room = Math.max(0, MAX_UNITS - (totalUnits - state.items[idx].quantity));
      const next = Math.min(qty, room);
      state.items[idx].quantity = next > 0 ? next : state.items[idx].quantity;
    }

    // sincroniza alias
    state.products = [...state.items];
    setJSON(STORAGE_CART_ITEMS, state.items);

    if (state.items.length > 0) {
      state.meta.cartTimestamp = Date.now();
      state.cartTimestamp = state.meta.cartTimestamp;
      setJSON(STORAGE_META, state.meta);
    }
  },

  UPDATE_ITEM_QTY(state, { codigo, quantity }) {
    const MAX_UNITS = getMaxUnits();
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const idx = state.items.findIndex(i => i?.product?.codigo === codigo);
    if (idx !== -1) {
      const totalOthers = state.items.reduce((t, i, k) => t + (k === idx ? 0 : (i?.quantity || 0)), 0);
      const room = Math.max(0, MAX_UNITS - totalOthers);
      const next = Math.min(qty, room);
      if (next > 0) {
        state.items[idx].quantity = next;
        state.products = [...state.items];
        setJSON(STORAGE_CART_ITEMS, state.items);
      }
    }
  },

  UPDATE_PRICE_AND_MARKUP(state, { codigo, precio_venta, precio_venta_web, markup }) {
    const idx = state.items.findIndex(i => i?.product?.codigo === codigo);
    if (idx !== -1) {
      const nuevo_precio = precio_venta ?? precio_venta_web;

      if (nuevo_precio !== undefined) {
        state.items[idx].product.precio_venta = nuevo_precio;
      }

      // opcional pero recomendable: mantener sincronizado si viene informado
      if (precio_venta_web !== undefined) {
        state.items[idx].product.precio_venta_web = precio_venta_web;
      }

      state.items[idx].product.markup = markup;
      state.products = [...state.items];
      setJSON(STORAGE_CART_ITEMS, state.items);
    }
  },

  CLEAR_REMOVED(state) {
    state.removed_items = [];
    state.removedProducts = [];
    removeKey(STORAGE_CART_REMOVED);
  },

  // Expiración (canónico)
  START_EXPIRATION_TIMER(state) { startExpirationTimerInternal(state); },
  VERIFY_AND_START_TIMER(state) {
    if (!import.meta.client) return;
    const minutes = getExpirationMinutes();
    const ts = state.meta.cartTimestamp;
    if (!ts) return;

    const expired = Date.now() > (ts + minutes * 60 * 1000);
    if (expired) {
      state.items = []; state.products = [];
      setJSON(STORAGE_CART_ITEMS, state.items);
      state.meta.cartTimestamp = null;
      state.cartTimestamp = null;
      setJSON(STORAGE_META, state.meta);
      clearTimer();
      return;
    }
    startExpirationTimerInternal(state);
  },

  TOUCH_TIMESTAMP(state) {
    state.meta.cartTimestamp = Date.now();
    state.cartTimestamp = state.meta.cartTimestamp;
    setJSON(STORAGE_META, state.meta);
  },

  // ────────────────────────────────────────────────────────
  // ALIAS 1:1 (mismos nombres del carrito original)
  // ────────────────────────────────────────────────────────
  updateProductPrice(state, payload) { mutations.UPDATE_PRICE_AND_MARKUP(state, payload); },
  addProducts(state, { selectedProduct, selectQuantity }) {
    mutations.ADD_ITEM(state, { product: selectedProduct, quantity: selectQuantity });
    mutations.TOUCH_TIMESTAMP(state);
    mutations.START_EXPIRATION_TIMER(state);
  },

  addRemovedProducts(state, product) {
    const removed = getJSON(STORAGE_CART_REMOVED, []);
    removed.push(product);
    setJSON(STORAGE_CART_REMOVED, removed);
    state.removed_items = removed;
    state.removedProducts = [...removed];
  },

  // Timer (alias)
  startExpirationTimer(state) { mutations.START_EXPIRATION_TIMER(state); },
  verifyCartTimestamp(state) { mutations.VERIFY_AND_START_TIMER(state); },

  addService(state, service) {
    const services = getJSON(STORAGE_SERVICES, []);
    const i = services.findIndex(s => s?.codigo === service?.codigo);
    if (i === -1) services.push(service);
    else services[i] = service;
    state.services = services;
    setJSON(STORAGE_SERVICES, services);
  },

  resetGifts(state) { state.gifts = []; setJSON(STORAGE_GIFTS, state.gifts); },
  resetRegaloRuleta(state) { state.regalo_ruleta = []; setJSON(STORAGE_REGALO_RULETA, state.regalo_ruleta); },

  resetGiftcardInput(state) { state.giftcardInput = ''; },
  giftcardLoader(state) { state.isGiftcardInfoLoading = !state.isGiftcardInfoLoading; },

  //--------------------------------------GETTERS-----------------------------------------------------------------//
  getProducts(state) {
    const list = getJSON(STORAGE_CART_ITEMS, []);
    state.items = list;
    state.products = [...list];
  },
  getRemovedProducts(state) {
    const removed = getJSON(STORAGE_CART_REMOVED, []);
    state.removed_items = removed;
    state.removedProducts = [...removed];
  },
  getCartTimestamp(state) { state.cartTimestamp = getJSON(STORAGE_META, {}).cartTimestamp ?? null; },

  gettransaction_uuid(state) { state.transaction_uuid = getJSON(STORAGE_META, {}).transaction_uuid ?? null; },

  // Paso a paso (alias)
  getPasoAPaso(state) { state.pasoapaso = getJSON(STORAGE_PASOAPASO, 0); },

  // Flags individuales (alias)
  getAcceptTerms(state) {
    const f = getJSON(STORAGE_FLAGS, {});
    state.acceptTerms = !!f.acceptTerms;
    state.flags.acceptTerms = state.acceptTerms;
  },

  getAcceptPolitica(state) {
    const f = getJSON(STORAGE_FLAGS, {});
    state.acceptPolitica = !!f.acceptPolitica;
    state.flags.acceptPolitica = state.acceptPolitica;
  },

  getBatteryInstallType(state) {
    const inst = getJSON(STORAGE_BATTERY_INSTALL_TYPE, {});
    state.batteryInstallType = inst || '';
    //console.log("getBatteryInstallType2: ", state.batteryInstallType);
  },

  // Selección de local (alias) + fallbacks legacy
  getLocalPivote(state) {
    state.localpivote = getJSON(STORAGE_LOCALPIVOTE, {}) || {};
    if (!state.localpivote?.local_id) {
      const legacy = getLegacyJSON('localpivote', {});
      if (legacy?.local_id) {
        state.localpivote = legacy;
        setJSON(STORAGE_LOCALPIVOTE, state.localpivote);
      }
    }
  },

  getFullcercano(state) {
    state.fullcercano = getJSON(STORAGE_FULLCERCANO, {}) || {};
    if (!state.fullcercano?.local_id) {
      const legacy = getLegacyJSON('fullcercano', {});
      if (legacy?.local_id) {
        state.fullcercano = legacy;
        setJSON(STORAGE_FULLCERCANO, state.fullcercano);
      }
    }
  },

  getlocalSeleccionado(state) { 
    state.localSeleccionado = getJSON(STORAGE_localSeleccionado, null); 
    //console.log("state.localSeleccionado: ", state.localSeleccionado);
  },

  getLocal_cambio(state) { state.local_cambio = !!getJSON(STORAGE_LOCAL_CAMBIO, false); },

  // Region/Commune (alias; aislado de cookies)
  getRegion(state) {
    const m = getJSON(STORAGE_MEASURE, {});
    state.region = m.region ?? state.address.region ?? null;
  },
  getCommune(state) {
    const m = getJSON(STORAGE_MEASURE, {});
    state.commune = m.commune ?? state.address.commune ?? null;
  },

  // Servicios (alias)
  getServicesStore(state) { state.services = getJSON(STORAGE_SERVICES, []); },
  // calendar
  getCalendar(state, scheduleType = 1) {
    const sch = getJSON(STORAGE_SCHEDULE, null);
    const base = sch || {
      checkStoreAvailability: false,
      scheduleType: parseInt(scheduleType),
      date: new Date().toISOString().slice(0, 10),
      time: null,
      totalTime: 0,
      tempario: "",
    };
    state.calendar = { ...state.calendar, ...base };
  },

  // Addresses (alias -> address/addresses)
  getAddresses(state) {
    const addresses = getJSON(STORAGE_ADDRESSES_OBJ, null) || state.addresses;
    state.addresses = { ...state.addresses, ...addresses };
    //console.log("state.addresses: ", state.addresses);
  },

  getPersonalInformation(state) {
    const p = getJSON(STORAGE_PERSONAL, null) || state.personalInformation;
    state.personalInformation = { ...state.personalInformation, ...p };
  },

  getPayment(state) { state.payment = getJSON(STORAGE_PAYMENT, state.payment); },

  getGiftcard(state) {
    const g = getJSON(STORAGE_GIFTCARD, null);
    if (g) state.giftcard = { ...state.giftcard, ...g };
  },

  getGifts(state) { state.gifts = getJSON(STORAGE_GIFTS, []); },
  getRegaloRuleta(state) { state.regalo_ruleta = getJSON(STORAGE_REGALO_RULETA, []); },

  getUbication_confirmed(state) {
    state.ubication_confirmed = { ...state.ubication_confirmed, ...getJSON(STORAGE_UBICATION_CONFIRMED, {}) };
  },

  getUbication(state) {
    state.ubication = { ...state.ubication, ...getJSON(STORAGE_UBICATION, {}) };
  },

  // AddressWasChanged (alias)
  getaAddressWasChanged(state) {
    state.addressWasChanged = !!getJSON(STORAGE_ADDRESS_WAS_CHANGED, false);
  },

  // Shopping step (alias)
  getShoppingStep(state) { state.shoppingStep = parseInt(getStr(STORAGE_SHOPPING_STEP, '0')) || 0; },
  //-----------------------------------------SETS-----------------------------------------------------------------//
  // Instalación / Agenda / Dirección / Local
  SET_INSTALL(state, payload) {
    state.batteryInstallType = { ...state.batteryInstallType, ...payload };
    state.batteryInstallType = state.batteryInstallType || "";
    setJSON(STORAGE_BATTERY_INSTALL_TYPE, state.batteryInstallType);
  },
  SET_localSeleccionado(state, sel) {
    state.localSeleccionado = { ...state.localSeleccionado, ...sel };
    setJSON(STORAGE_localSeleccionado, state.localSeleccionado);
  },
  SET_CALENDARIO(state, payload) {
    state.calendar = { ...state.calendar, ...payload };
    setJSON(STORAGE_SCHEDULE, state.calendar);
  },
  SET_ADDRESS(state, payload) {
    state.address = { ...state.address, ...payload };
    // refleja en addresses.address
    state.addresses.address = { ...state.addresses.address, ...state.address };
    setJSON(STORAGE_ADDRESS, state.address);
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
  },

  // Datos personales
  SET_PERSONAL(state, payload) {
    state.personalInformation = { ...state.personalInformation, ...payload };
    setJSON(STORAGE_PERSONAL, state.personalInformation);
  },

  // Payment
  SET_PAYMENT(state, payment) {
    state.payment = { ...state.payment, ...payment };
    setJSON(STORAGE_PAYMENT, state.payment);
  },

  // Flags y meta
  SET_FLAGS(state, payload) {
    state.flags = { ...state.flags, ...payload };
    state.acceptTerms       = !!state.flags.acceptTerms;
    state.acceptPolitica    = !!state.flags.acceptPolitica;
    state.personalInfoSaved = !!state.flags.personalInfoSaved;
    setJSON(STORAGE_FLAGS, state.flags);
  },
  SET_TRANSACTION_UUID(state, uuid) {
    state.meta.transaction_uuid = uuid || null;
    state.transaction_uuid = state.meta.transaction_uuid;
    setJSON(STORAGE_META, state.meta);
  },
  SET_IS_VALID_CARPLATE(state, isValid) {
    state.meta.isValidCarplate = !!isValid;
    state.isValidCarplate = state.meta.isValidCarplate;
    setJSON(STORAGE_META, state.meta);
  },
  setProducts(state, products) {
    state.items = Array.isArray(products) ? products : [];
    state.products = [...state.items];
    setJSON(STORAGE_CART_ITEMS, state.items);
  },
  setProductsQuantity(state, { product, quantity }) {
    if (!product?.codigo) return;
    mutations.UPDATE_ITEM_QTY(state, { codigo: product.codigo, quantity });
  },
  setCartTimestamp(state, cartTimestamp) {
    state.meta.cartTimestamp = parseInt(cartTimestamp, 10) || Date.now();
    state.cartTimestamp = state.meta.cartTimestamp;
    setJSON(STORAGE_META, state.meta);
  },
  settransaction_uuid(state, uuid) { mutations.SET_TRANSACTION_UUID(state, uuid); },
  setPasoAPaso(state, paso) { mutations.SET_PASOAPASO(state, paso); },
  setAcceptTerms(state, acceptTerms) { mutations.SET_FLAGS(state, { acceptTerms: !!acceptTerms }); },
  setAcceptPolitica(state, acceptPolitica) { mutations.SET_FLAGS(state, { acceptPolitica: !!acceptPolitica }); },
  setPersonalInfoSaved(state, saved) { mutations.SET_FLAGS(state, { personalInfoSaved: !!saved }); },

  // Battery install type (alias plano)
  setBatteryInstallType(state, batteryInstallType) {
    //console.log("setBatteryInstallType: ", batteryInstallType);
    state.batteryInstallType = batteryInstallType || '';
    //console.log("cambiando el valor de batteryInstallType en el store: ", state.batteryInstallType);
    setJSON(STORAGE_BATTERY_INSTALL_TYPE, state.batteryInstallType);
  },
  setLocalPivote(state, localpivote) {
    state.localpivote = localpivote || {};
    setJSON(STORAGE_LOCALPIVOTE, state.localpivote);
  },

  setFullcercano(state, fullcercano) {
    state.fullcercano = fullcercano || {};
    setJSON(STORAGE_FULLCERCANO, state.fullcercano);
  },
  setlocalSeleccionado(state, local) {
    state.localSeleccionado = local || null;
    setJSON(STORAGE_localSeleccionado, state.localSeleccionado);
  },
  setLocal_cambio(state, local_cambio) {
    state.local_cambio = !!local_cambio;
    setJSON(STORAGE_LOCAL_CAMBIO, state.local_cambio);
  },
  setRegion(state, region) {
    const m = getJSON(STORAGE_MEASURE, {});
    const next = { ...m, region };
    setJSON(STORAGE_MEASURE, next);
    state.region = region;
    state.address.region = region;
    setJSON(STORAGE_ADDRESS, state.address);
  },
  setCommune(state, commune) {
    const m = getJSON(STORAGE_MEASURE, {});
    const next = { ...m, commune };
    setJSON(STORAGE_MEASURE, next);
    state.commune = commune;
    state.address.commune = commune;
    setJSON(STORAGE_ADDRESS, state.address);
  },
  setCheckStoreAvailability(state, { checkStoreAvailability, scheduleType = 1 }) {
    const next = { ...state.calendar, checkStoreAvailability, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setScheduleType(state, scheduleType = 1) {
    const next = { ...state.calendar, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setDate(state, { date, scheduleType = 1 }) {
    const next = { ...state.calendar, date, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setTime(state, { time, scheduleType = 1 }) {
    const next = { ...state.calendar, time, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setTotalTime(state, { totalTime, scheduleType = 1 }) {
    const next = { ...state.calendar, totalTime, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setCalendarToDefault(state, scheduleType = 1) {
    const next = {
      checkStoreAvailability: false,
      scheduleType: parseInt(scheduleType),
      date: new Date().toISOString().slice(0, 10),
      time: null,
      totalTime: 0,
      tempario: "",
    };
    mutations.SET_CALENDARIO(state, next);
  },
  setTempario(state, { tempario, scheduleType = 1 }) {
    const next = { ...state.calendar, tempario, scheduleType: parseInt(scheduleType) };
    mutations.SET_CALENDARIO(state, next);
  },
  setShowAddressOptions(state, showAddressOptions) {
    state.addresses.showAddressOptions = !!showAddressOptions;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
  },
  setShowLeaveAddress(state, showLeaveAddress) {
    state.addresses.showLeaveAddress = !!showLeaveAddress;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
  },
  setAddress(state, address) {
    state.addresses.address = { ...state.addresses.address, ...address };
    mutations.SET_ADDRESS(state, state.addresses.address);
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
  },
  setLeaveAddress(state, leaveAddress) {
    state.addresses.leaveAddress = { ...state.addresses.leaveAddress, ...leaveAddress };
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
  },
  setAddressRegion(state, { region, type }) {
    if (type === 'address') state.addresses.address.region = region;
    else state.addresses.leaveAddress.region = region;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },
  setAddressCommune(state, { commune, type }) {
    if (type === 'address') state.addresses.address.commune = commune;
    else state.addresses.leaveAddress.commune = commune;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },
  setAddressAddress(state, { address, type }) {
    if (type === 'address') state.addresses.address.address = address;
    else state.addresses.leaveAddress.address = address;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },
  setAddressDetails(state, { details, type }) {
    if (type === 'address') state.addresses.address.details = details;
    else state.addresses.leaveAddress.details = details;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },
  setAddressLatitudeAndLongitude(state, { latitude, longitude, type }) {
    if (type === 'address') {
      state.addresses.address.latitude = latitude;
      state.addresses.address.longitude = longitude;
    } else {
      state.addresses.leaveAddress.latitude = latitude;
      state.addresses.leaveAddress.longitude = longitude;
    }
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },
  setAddressComments(state, { comments, type }) {
    if (type === 'address') state.addresses.address.comments = comments;
    else state.addresses.leaveAddress.comments = comments;
    setJSON(STORAGE_ADDRESSES_OBJ, state.addresses);
    if (type === 'address') mutations.SET_ADDRESS(state, state.addresses.address);
  },

  // Datos personales (alias)
  setPersonalInformation(state, personalInformation) {
    state.personalInformation = { ...state.personalInformation, ...personalInformation };
    mutations.SET_PERSONAL(state, state.personalInformation);
  },

  // Payment (alias)
  setPayment(state, payment) { mutations.SET_PAYMENT(state, payment); },

  // Giftcard (alias)
  setGiftcard(state, giftcard) {
    state.giftcard = { ...state.giftcard, ...giftcard };
    setJSON(STORAGE_GIFTCARD, state.giftcard);
  },

  setGifts(state, gifts) { state.gifts = Array.isArray(gifts) ? gifts : []; setJSON(STORAGE_GIFTS, state.gifts); },
  setRegaloRuleta(state, regalo) { state.regalo_ruleta = Array.isArray(regalo) ? regalo : []; setJSON(STORAGE_REGALO_RULETA, state.regalo_ruleta); },
 
  setgiftcardInput(state, value) { state.giftcardInput = value ?? ''; },
  set_giftcardLoader(state, value) { state.isGiftcardInfoLoading = !!value; },

  // Ubicación (alias)
  setUbication_confirmed(state, ubication) {
    state.ubication_confirmed = { ...state.ubication_confirmed, ...ubication };
    setJSON(STORAGE_UBICATION_CONFIRMED, state.ubication_confirmed);
  },

  setUbication(state, ubication) {
    state.ubication = { ...state.ubication, ...ubication };
    setJSON(STORAGE_UBICATION, state.ubication);
  },
  setUbicationAddress(state, address) {
    state.ubication.address = address || "";
    setJSON(STORAGE_UBICATION, state.ubication);
  },

  setShoppingStep(state, step) {
    state.shoppingStep = parseInt(step, 10) || 0;
    setStr(STORAGE_SHOPPING_STEP, state.shoppingStep);
  },

  setUltimo_paso(state, ultimo_paso) {
    state.ultimo_paso = parseInt(ultimo_paso, 10) || 0;
    setStr(STORAGE_ULTIMO_PASO, state.ultimo_paso);
  },

  //--------------------------------------REMOVE-----------------------------------------------------------------//
  REMOVE_ITEM(state, codigo) {
    const idx = state.items.findIndex(i => i?.product?.codigo === codigo);
    if (idx !== -1) {
      const removed = getJSON(STORAGE_CART_REMOVED, []);
      removed.push(state.items[idx]);
      setJSON(STORAGE_CART_REMOVED, removed);
      state.removed_items = removed;
      state.removedProducts = [...removed];

      state.items.splice(idx, 1);
      state.products = [...state.items];
      setJSON(STORAGE_CART_ITEMS, state.items);
    }
  },

  removeProduct(state, product) { if (product?.codigo) mutations.REMOVE_ITEM(state, product.codigo); },
  removeProductByCode(state, code) { mutations.REMOVE_ITEM(state, code); },
  removeAllRemovedProducts(state) {
    state.removed_items = [];
    state.removedProducts = [];
    removeKey(STORAGE_CART_REMOVED);
  },
  removeAllProducts(state) {
    state.items = []; state.products = [];
    setJSON(STORAGE_CART_ITEMS, state.items);
  },

  removelocalSeleccionado(state) {
    state.localSeleccionado = null;
    removeKey(STORAGE_localSeleccionado);
  },

  removeService(state, service) {
    const services = getJSON(STORAGE_SERVICES, []);
    const i = services.findIndex(s => s?.codigo === service?.codigo);
    if (i !== -1) services.splice(i, 1);
    state.services = services;
    setJSON(STORAGE_SERVICES, services);
  },
  removeAllServices(state) { state.services = []; removeKey(STORAGE_SERVICES); },


  removeGiftcard(state) {
    state.giftcard = { itWasSend:false, isValid:false, code:"", uuid:"", descriptionCode:"" };
    removeKey(STORAGE_GIFTCARD);
  },
  // Limpieza localStorage (alias, aislada al namespace)
  removeAllLocalStorages(state) {
    [STORAGE_META, STORAGE_SHOPPING_STEP, STORAGE_SERVICES, STORAGE_SCHEDULE, STORAGE_GIFTCARD, STORAGE_GIFTS, STORAGE_REGALO_RULETA].forEach(k => removeKey(k));

    state.cartTimestamp = null;
    state.meta.cartTimestamp = null;
    state.shoppingStep = 0;
    state.services = [];
    state.calendar = {
      checkStoreAvailability: false,
      scheduleType: 1,
      date: new Date().toISOString().slice(0, 10),
      time: null,
      totalTime: 0,
      tempario: "",
    };
    state.giftcard = { itWasSend:false, isValid:false, code:"", uuid:"", descriptionCode:"" };
    state.gifts = [];
    state.regalo_ruleta = [];

    clearTimer();
  },
};

// GETTERS (opcionales)
const getters = {
  getCart: (state) => state.items,
  getRemoved: (state) => state.removed_items,
  getCountDistinct: (state) => state.items.length,
  getCountUnits: (state) => state.items.reduce((t, i) => t + (i?.quantity || 0), 0),
  getInstall: (state) => state.batteryInstallType,
  getlocalSeleccionado: (state) => state.localSeleccionado,
  getcalendar: (state) => state.calendar,
  getAddress: (state) => state.address,
  getPersonal: (state) => state.personalInformation,
  getPayment: (state) => state.payment,
  getFlags: (state) => state.flags,
  getPasoAPaso: (state) => state.pasoapaso,
  getMeta: (state) => state.meta,
  isExpired: (state) => {
    if (!state.meta.cartTimestamp) return true;
    const minutes = getExpirationMinutes();
    return Date.now() > (state.meta.cartTimestamp + minutes * 60 * 1000);
  },
  getFullCheckout: (state) => ({
    items: state.items,
    install: state.batteryInstallType,
    localSeleccionado: state.localSeleccionado,
    calendar: state.calendar,
    address: state.address,
    personalInformation: state.personalInformation,
    payment: state.payment,
    flags: state.flags,
    meta: state.meta,
    pasoapaso: state.pasoapaso,
  }),
};

// ACTIONS
const actions = {
  addItemAndStart({ commit }, payload) {
    commit('ADD_ITEM', payload);
    commit('TOUCH_TIMESTAMP');
    commit('START_EXPIRATION_TIMER');
  },
  replaceItem({ commit }, payload) {
    commit('RESET_CART');
    commit('ADD_ITEM', payload);
    commit('TOUCH_TIMESTAMP');
    commit('START_EXPIRATION_TIMER');
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
